import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Utils, authJsonHeaders, formatDate, handleSessionExpired, getBillingStatusDisplay, PLAN_TYPE_CODES, DEFAULT_BILLING_CURRENCY, isBillingStatus } from '../../utils';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { useActionDropdown } from '../../hooks/useActionDropdown';
import ListPaginationBar from '../common/ListPaginationBar';
import Modal from '../common/Modal';
import AdminUserPlanChangeModal from './AdminUserPlanChangeModal';
import AdminUserPricingDisplay from './AdminUserPricingDisplay';
import AdminUserCustomPricing from './AdminUserCustomPricing';
import { MdSearch, MdDelete, MdVisibility, MdPeople, MdPayment, MdWarning, MdApps, MdDevices, MdErrorOutline, MdCheckCircle, MdWarningAmber, MdTune, MdBlock, MdLockOpen, MdSettings } from 'react-icons/md';

const AdminUsers = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState('all');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
    const [selectedUser, setSelectedUser] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [summary, setSummary] = useState(null);
    const [deleteModal, setDeleteModal] = useState({ show: false, user: null, loading: false });
    const [alertModal, setAlertModal] = useState({ show: false, type: '', title: '', message: '', apps: [] });
    const [planChangeModal, setPlanChangeModal] = useState({ show: false, requestId: null, userLabel: '' });
    const [pricingEditModal, setPricingEditModal] = useState({ show: false, user: null });
    const [userSettingsModal, setUserSettingsModal] = useState({
        show: false, user: null, loading: false, saving: false, showUsageBill: false,
    });

    const tableScrollRef = useRef(null);
    const {
        openActionItem,
        dropdownPosition,
        dropdownRef,
        closeDropdown,
        handleDropdownAction,
        handleOpenDropdown,
    } = useActionDropdown({
        tableScrollRef,
        optionCount: (user) => getUserActionOptions(user).length,
        menuWidth: 220,
        useAnchored: true,
    });

    useEffect(() => {
        fetchSummary();
    }, []);

    useEffect(() => {
        fetchUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination.page, search, filter]);

    const fetchSummary = async () => {
        try {
            const response = await fetch(Utils.baseurl + 'admin/users/billing-summary', {
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) setSummary(result.data);
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const response = await fetch(Utils.baseurl + 'admin/users', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ page: pagination.page, limit: pagination.limit, search, filter })
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                setUsers(result.data);
                setPagination(prev => ({ ...prev, ...result.pagination }));
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
        setLoading(false);
    };

    const openDeleteModal = (user) => {
        closeDropdown();
        setDeleteModal({ show: true, user, loading: false });
    };

    const closeDeleteModal = () => {
        setDeleteModal({ show: false, user: null, loading: false });
    };

    const handleDelete = async () => {
        if (!deleteModal.user) return;
        setDeleteModal(prev => ({ ...prev, loading: true }));
        try {
            const response = await fetch(Utils.baseurl + 'admin/users/delete', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ userId: deleteModal.user.id })
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            closeDeleteModal();
            if (result.status) {
                setAlertModal({
                    show: true,
                    type: 'success',
                    title: 'User Deleted Successfully',
                    message: `The user "${deleteModal.user.first_name || ''} ${deleteModal.user.last_name || ''}" has been permanently removed from the platform.`,
                    apps: []
                });
                fetchUsers();
                fetchSummary();
            } else if (result.canDelete === false) {
                setAlertModal({
                    show: true,
                    type: 'error',
                    title: 'Cannot Delete User',
                    message: result.message,
                    apps: result.appsWithActiveUsers || []
                });
            } else {
                setAlertModal({
                    show: true,
                    type: 'error',
                    title: 'Deletion Failed',
                    message: result.message || 'An error occurred while deleting the user.',
                    apps: []
                });
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            closeDeleteModal();
            setAlertModal({
                show: true,
                type: 'error',
                title: 'Deletion Failed',
                message: 'An unexpected error occurred. Please try again later.',
                apps: []
            });
        }
    };

    const viewUserDetails = async (userId) => {
        closeDropdown();
        try {
            const response = await fetch(Utils.baseurl + 'admin/users/detail', {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ userId })
            });
            const result = await response.json();
            if (handleSessionExpired(response, result)) return;
            if (result.status) {
                setSelectedUser(result.data);
                setShowModal(true);
            }
        } catch (error) {
            console.error('Error fetching user details:', error);
        }
    };

    const openPlanChangeModal = (user) => {
        closeDropdown();
        if (!user.pending_plan_change?.id) return;
        const label = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email;
        setPlanChangeModal({ show: true, requestId: user.pending_plan_change.id, userLabel: label });
    };

    const openPricingEditModal = (user) => {
        closeDropdown();
        setPricingEditModal({ show: true, user });
    };

    const openUserSettingsModal = async (user) => {
        closeDropdown();
        setUserSettingsModal({ show: true, user, loading: true, saving: false, showUsageBill: false });
        try {
            const res = await fetch(`${Utils.baseurl}admin/users/settings-get`, {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ userId: user.id }),
            });
            const result = await res.json();
            if (handleSessionExpired(res, result)) return;
            setUserSettingsModal((prev) => ({
                ...prev,
                loading: false,
                showUsageBill: Boolean(result.status && result.data?.show_usage_bill),
            }));
        } catch (e) {
            console.error(e);
            setUserSettingsModal((prev) => ({ ...prev, loading: false }));
        }
    };

    const closeUserSettingsModal = () => {
        setUserSettingsModal({ show: false, user: null, loading: false, saving: false, showUsageBill: false });
    };

    const saveUserSettings = async () => {
        const { user, showUsageBill } = userSettingsModal;
        if (!user?.id) return;
        setUserSettingsModal((prev) => ({ ...prev, saving: true }));
        try {
            const res = await fetch(`${Utils.baseurl}admin/users/settings-save`, {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ userId: user.id, show_usage_bill: showUsageBill ? 1 : 0 }),
            });
            const result = await res.json();
            if (handleSessionExpired(res, result)) return;
            if (result.status) {
                toast.success(result.message || 'User settings saved');
                closeUserSettingsModal();
            } else {
                toast.error(result.message || 'Save failed');
                setUserSettingsModal((prev) => ({ ...prev, saving: false }));
            }
        } catch (e) {
            console.error(e);
            toast.error('Save failed');
            setUserSettingsModal((prev) => ({ ...prev, saving: false }));
        }
    };


    const toggleBlockBilling = async (user) => {
        closeDropdown();
        // Don't bail out when the user has no subscription row yet (e.g. still on the
        // free plan) — the backend creates a default free-plan subscription on the fly
        // (assignDefaultFreePlan) when one doesn't exist, so Backs mode must work for them too.
        const isBlocked = isBillingStatus(user.sub?.status, 1);

        const confirmResult = await Swal.fire({
            title: 'Are you sure?',
            text: isBlocked
                ? 'This will turn Backs mode off and resume normal billing for this user.'
                : 'This will turn Backs mode on for this user.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: isBlocked ? 'Yes, turn off' : 'Yes, turn on',
        });
        if (!confirmResult.isConfirmed) {
            return;
        }

        try {
            const res = await fetch(`${Utils.baseurl}admin/users/billing-status-set`, {
                method: 'POST',
                headers: authJsonHeaders(Utils.storageKeys.adminToken),
                body: JSON.stringify({ userId: user.id, block: !isBlocked }),
            });
            const result = await res.json();
            if (handleSessionExpired(res, result)) return;
            if (result.status) {
                toast.success(result.message || 'Billing status updated');
                fetchUsers();
            } else {
                toast.error(result.message || 'Failed to update billing status');
            }
        } catch (e) {
            console.error(e);
            toast.error('Failed to update billing status');
        }
    };

    const formatCurrency = (amount) => {
        const sym = DEFAULT_BILLING_CURRENCY?.symbol;
        return sym + (parseFloat(amount) || 0).toLocaleString('en-IN');
    };

    const getPlanLabel = (user) => user.plan_display || 'Free';

    const getDaysUntilBilling = (date) => {
        if (!date) return null;
        return Math.ceil((new Date(date) - new Date()) / (1000 * 60 * 60 * 24));
    };

    const getUserActionOptions = (user) => {
        const options = [];
        const planType = Number(user.sub?.plan_type);
        const isBlocked = isBillingStatus(user.sub?.status, 1);

        options.push({ key: 'view', label: 'View details', icon: <MdVisibility size={18} />, action: () => viewUserDetails(user.id) });

        if (user.pending_plan_change?.id) {
            options.push({ key: 'pricing', label: 'Approve request', icon: <MdTune size={18} />, action: () => openPlanChangeModal(user) });
        } else if (planType === PLAN_TYPE_CODES[2].value) {
            options.push({ key: 'edit-pricing', label: 'Manage pricing', icon: <MdTune size={18} />, action: () => openPricingEditModal(user) });
        }

        options.push({
            key: 'user-settings',
            label: 'User settings',
            icon: <MdSettings size={18} />,
            action: () => openUserSettingsModal(user),
        });

        // Backs mode — shown for all users regardless of plan
        options.push({
            key: 'block',
            label: isBlocked ? 'Backs mode off' : 'Backs mode on',
            icon: isBlocked ? <MdLockOpen size={18} /> : <MdBlock size={18} />,
            action: () => toggleBlockBilling(user),
        });

        options.push({ key: 'delete', label: 'Delete user', icon: <MdDelete size={18} />, action: () => openDeleteModal(user), danger: true });
        return options;
    };

    const SummaryCard = ({ icon, title, value, subtitle, color }) => (
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)', flex: 1, minWidth: '180px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ background: color + '20', padding: '10px', borderRadius: '10px' }}>
                    {icon}
                </div>
            </div>
            <div style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a' }}>{value}</div>
            <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>{title}</div>
            {subtitle && <div style={{ fontSize: '12px', color: color, marginTop: '4px' }}>{subtitle}</div>}
        </div>
    );

    return (
        <div>
            <div style={{ marginBottom: '24px' }}>
                <h1 style={{ fontSize: '28px', fontWeight: '700', color: '#1a1a1a', margin: 0 }}>Platform Users</h1>
                <p style={{ color: '#6b7280', marginTop: '8px' }}>Manage users, billing & subscriptions</p>
            </div>

            {summary && (
                <div style={{ display: 'flex', gap: '16px', marginBottom: '24px', flexWrap: 'wrap' }}>
                    <SummaryCard icon={<MdPeople size={24} color="#4f46e5" />} title="Total Users" value={summary.totalUsers} subtitle={`${summary.activeUsers || 0} active, ${summary.paidUsers} paid`} color="#4f46e5" />
                    <SummaryCard icon={<MdPayment size={24} color="#059669" />} title="Total Revenue" value={formatCurrency(summary.totalRevenue)} subtitle={`${summary.activeSubscribers} active subscribers`} color="#059669" />
                    <SummaryCard icon={<MdWarning size={24} color="#d97706" />} title="Billing Due Soon" value={summary.billingDueSoon} subtitle="Next 7 days" color="#d97706" />
                    <SummaryCard icon={<MdApps size={24} color="#7c3aed" />} title="Applications" value={summary.totalApplications} color="#7c3aed" />
                    <SummaryCard icon={<MdDevices size={24} color="#0891b2" />} title="Device Subscriptions" value={summary.totalSubscriptions} subtitle={`${summary.activeDeviceSubscriptions} active`} color="#0891b2" />
                </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {[
                        { key: 'all', label: 'All Users' },
                        { key: 'paid', label: 'Paid' },
                        { key: 'free', label: 'Free' },
                        { key: 'plan_request', label: 'Plan Change Request' },
                        { key: 'active', label: 'Active' },
                        { key: 'backs_mode', label: 'Backs mode' },
                        { key: 'suspended', label: 'Suspended' },
                        { key: 'billing_due', label: 'Billing Due' }
                    ].map(f => (
                        <button
                            key={f.key}
                            onClick={() => { setFilter(f.key); setPagination(p => ({ ...p, page: 1 })); }}
                            style={{
                                padding: '8px 16px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
                                background: filter === f.key ? '#4f46e5' : 'white',
                                color: filter === f.key ? 'white' : '#6b7280',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                            }}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'white', padding: '8px 16px', borderRadius: '8px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
                    <MdSearch size={20} color="#9ca3af" />
                    <input
                        type="text"
                        placeholder="Search users..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
                        style={{ border: 'none', outline: 'none', fontSize: '14px', width: '200px' }}
                    />
                </div>
            </div>

            <div style={{ background: 'white', borderRadius: '16px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                <div ref={tableScrollRef} style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
                        <thead>
                            <tr style={{ background: '#f9fafb' }}>
                                <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' }}>User</th>
                                <th style={{ padding: '14px 16px', textAlign: 'left', fontWeight: '600', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' }}>Plan</th>
                                <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' }}>Billing Cycle</th>
                                <th style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' }}>Payment Received</th>
                                <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' }}>Status</th>
                                <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' }}>Next Bill</th>
                                <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' }}>Apps</th>
                                <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' }}>Joined</th>
                                <th style={{ padding: '14px 16px', textAlign: 'center', fontWeight: '600', color: '#374151', fontSize: '13px', whiteSpace: 'nowrap' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
                            ) : users.length === 0 ? (
                                <tr><td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No users found</td></tr>
                            ) : users.map(user => {
                                const sub = user.sub;
                                const daysUntilBilling = getDaysUntilBilling(sub?.next_billing_date);
                                const isBillingDueSoon = daysUntilBilling !== null && daysUntilBilling <= 7;
                                const isBacksMode = isBillingStatus(sub?.status, 1);
                                const billingDisplay = getBillingStatusDisplay(sub?.status);
                                const planType = Number(sub?.plan_type);
                                const isPaidPlan = planType === PLAN_TYPE_CODES[1].value || planType === PLAN_TYPE_CODES[2].value;

                                return (
                                    <tr key={user.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                        <td style={{ padding: '14px 16px' }}>
                                            <div style={{ fontWeight: '500', color: '#1a1a1a' }}>{user.first_name || ''} {user.last_name || ''}</div>
                                            <div style={{ fontSize: '12px', color: '#6b7280' }}>{user.email}</div>
                                            {user.pending_plan_change ? (
                                                <span style={{ display: 'inline-block', marginTop: '4px', fontSize: '10px', background: '#fef3c7', color: '#92400e', padding: '2px 8px', borderRadius: '10px' }}>
                                                    Plan change pending
                                                </span>
                                            ) : null}
                                        </td>
                                        <td style={{ padding: '14px 16px' }}>
                                            <span style={{
                                                padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500',
                                                background: isPaidPlan ? '#dbeafe' : '#f3f4f6',
                                                color: isPaidPlan ? '#1d4ed8' : '#6b7280'
                                            }}>
                                                {getPlanLabel(user)}
                                            </span>
                                        </td>
                                        <td style={{ padding: '14px 16px', textAlign: 'center', color: '#374151', fontSize: '13px' }}>
                                            {user.billing_period?.start || user.billing_period?.end ? (
                                                <div>
                                                    <div style={{ color: '#374151', whiteSpace: 'nowrap' }}>
                                                        {formatDate(user.billing_period.start)} – {formatDate(user.billing_period.end)}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: 2, textTransform: 'capitalize' }}>
                                                        {user.billing_cycle_label || sub?.billing_cycle || 'monthly'}
                                                    </div>
                                                </div>
                                            ) : (
                                                <span style={{ textTransform: 'capitalize' }}>{sub?.billing_cycle || 'monthly'}</span>
                                            )}
                                        </td>
                                        <td style={{ padding: '14px 16px', textAlign: 'right', fontWeight: '600', color: '#059669', fontSize: '13px' }}>
                                            {Number(user.payment_received ?? sub?.total_payments) > 0
                                                ? formatCurrency(user.payment_received ?? sub.total_payments)
                                                : '-'}
                                        </td>
                                        <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                                            {(isPaidPlan || isBacksMode) ? (
                                                <span className={billingDisplay.badgeClass || 'badge rounded-pill bg-light-secondary'}>
                                                    {billingDisplay.label}
                                                </span>
                                            ) : '-'}
                                        </td>
                                        <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: '13px' }}>
                                            {sub?.next_billing_date ? (
                                                <div>
                                                    <div style={{ color: isBillingDueSoon ? '#d97706' : '#374151' }}>{formatDate(sub.next_billing_date)}</div>
                                                    {daysUntilBilling !== null && (
                                                        <div style={{ fontSize: '11px', color: isBillingDueSoon ? '#d97706' : '#9ca3af' }}>
                                                            {daysUntilBilling <= 0 ? 'Overdue' : `${daysUntilBilling} days`}
                                                        </div>
                                                    )}
                                                </div>
                                            ) : '-'}
                                        </td>
                                        <td style={{ padding: '14px 16px', textAlign: 'center', color: '#374151', fontWeight: '500' }}>{user.apps_count || 0}</td>
                                        <td style={{ padding: '14px 16px', textAlign: 'center', color: '#6b7280', fontSize: '13px' }}>
                                            {formatDate(user.createdAt)}
                                        </td>
                                        <td style={{ padding: '14px 16px', textAlign: 'center' }} className="col-action">
                                            <button
                                                type="button"
                                                className="dropdown-trigger-btn btn btn-link p-0"
                                                onClick={(e) => handleOpenDropdown(e, user.id, user)}
                                                aria-label="User actions"
                                            >
                                                <svg viewBox="0 0 16 16" fill="currentColor" width="16" height="16">
                                                    <path d="M8 5.25A1.625 1.625 0 108 2a1.625 1.625 0 000 3.25zM8 9.625a1.625 1.625 0 100-3.25 1.625 1.625 0 000 3.25zM9.625 12.375a1.625 1.625 0 11-3.25 0 1.625 1.625 0 013.25 0z" />
                                                </svg>
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                <ListPaginationBar
                    page={pagination.page}
                    setPage={(n) => setPagination((p) => ({ ...p, page: n }))}
                    total={pagination.total}
                    pageSize={pagination.limit}
                    totalPages={pagination.totalPages}
                    ariaLabel="Admin users pagination"
                    className="px-3 py-3"
                    style={{ borderTop: '1px solid #f3f4f6' }}
                    loading={loading}
                />
            </div>

            {showModal && selectedUser && (
                <Modal title="User Details" onClose={() => setShowModal(false)} maxWidth="720px">
                    <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#6b7280', textTransform: 'uppercase' }}>Basic Information</h3>
                        <div style={{ fontSize: 13 }}>
                            <div className="row">
                                <div className="col-6 mb-2"><span style={{ color: '#6b7280' }}>Name:</span> <strong>{selectedUser.first_name} {selectedUser.last_name}</strong></div>
                                <div className="col-6 mb-2"><span style={{ color: '#6b7280' }}>Email:</span> <strong>{selectedUser.email}</strong></div>
                                <div className="col-6 mb-2"><span style={{ color: '#6b7280' }}>Mobile:</span> <strong>{selectedUser.mobile || 'N/A'}</strong></div>
                                <div className="col-6 mb-2"><span style={{ color: '#6b7280' }}>Role:</span> <strong>{selectedUser.role?.name || selectedUser.role_name || (typeof selectedUser.role === 'string' ? selectedUser.role : null) || 'N/A'}</strong></div>
                                <div className="col-6 mb-2"><span style={{ color: '#6b7280' }}>Joined:</span> <strong>{formatDate(selectedUser.createdAt)}</strong></div>
                            </div>
                        </div>
                    </div>

                    <div style={{ background: '#fffbeb', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#92400e', textTransform: 'uppercase' }}>Pricing</h3>
                        <AdminUserPricingDisplay user={selectedUser} />
                    </div>

                    <div style={{ background: '#eff6ff', borderRadius: '12px', padding: '20px', marginBottom: '8px' }}>
                        <h3 style={{ margin: '0 0 16px 0', fontSize: '14px', color: '#1d4ed8', textTransform: 'uppercase' }}>Organizations & Applications</h3>
                        <div style={{ fontSize: 13 }}>
                            <div className="row">
                                <div className="col-6 mb-2"><span style={{ color: '#6b7280' }}>Total App Users:</span> <strong>{selectedUser.totalAppUsers || 0}</strong></div>
                                <div className="col-6 mb-2"><span style={{ color: '#6b7280' }}>Active App Users:</span> <strong style={{ color: '#059669' }}>{selectedUser.activeAppUsers || 0}</strong></div>

                                <div className="col-6 mb-2">
                                    <span style={{ color: '#6b7280' }}>Organizations:</span> <strong>{selectedUser.organizations?.length || 0}</strong>

                                    {selectedUser.organizations?.length > 0 && (
                                        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {selectedUser.organizations.map(org => (
                                                <span key={org.id} style={{ background: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 12 }}>
                                                    {org.oganization_name}
                                                </span>
                                            ))}
                                        </div>
                                    )}

                                </div>
                                <div className="col-6 mb-2">
                                    <span style={{ color: '#6b7280' }}>Applications:</span> <strong>{selectedUser.applications?.length || 0}</strong>
                                    {selectedUser.applications?.length > 0 && (
                                        <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                            {selectedUser.applications.map(app => (
                                                <span key={app.id} style={{ background: 'white', padding: '4px 12px', borderRadius: 20, fontSize: 12 }}>
                                                    {app.application_name}
                                                </span>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}

            {planChangeModal.show && planChangeModal.requestId && (
                <AdminUserPlanChangeModal
                    requestId={planChangeModal.requestId}
                    userLabel={planChangeModal.userLabel}
                    onClose={() => setPlanChangeModal({ show: false, requestId: null, userLabel: '' })}
                    onSuccess={() => { fetchUsers(); fetchSummary(); }}
                />
            )}

            {pricingEditModal.show && pricingEditModal.user && (
                <Modal
                    title={`Manage pricing — ${pricingEditModal.user.email}`}
                    onClose={() => setPricingEditModal({ show: false, user: null })}
                    maxWidth="640px"
                >
                    <AdminUserCustomPricing
                        userId={pricingEditModal.user.id}
                        onSaved={() => { setPricingEditModal({ show: false, user: null }); fetchUsers(); }}
                    />
                </Modal>
            )}

            {userSettingsModal.show && userSettingsModal.user && (
                <Modal
                    title={`User settings — ${userSettingsModal.user.email}`}
                    onClose={closeUserSettingsModal}
                    maxWidth="640px"
                    footer={(
                        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', width: '100%' }}>
                            <button
                                type="button"
                                onClick={closeUserSettingsModal}
                                disabled={userSettingsModal.saving}
                                style={{ padding: '10px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={saveUserSettings}
                                disabled={userSettingsModal.saving || userSettingsModal.loading}
                                style={{ padding: '10px 18px', background: '#4f46e5', color: '#fff', border: 'none', borderRadius: 8, cursor: (userSettingsModal.saving || userSettingsModal.loading) ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600, opacity: (userSettingsModal.saving || userSettingsModal.loading) ? 0.7 : 1 }}
                            >
                                {userSettingsModal.saving ? 'Saving…' : 'Save'}
                            </button>
                        </div>
                    )}
                >
                    {userSettingsModal.loading ? (
                        <p style={{ textAlign: 'center', color: '#9ca3af', padding: '32px 0', margin: 0 }}>Loading…</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                            <div style={{ background: '#f9fafb', borderRadius: 10, padding: '14px 16px' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                                    Billing
                                </div>
                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={userSettingsModal.showUsageBill}
                                        onChange={(e) => setUserSettingsModal((prev) => ({ ...prev, showUsageBill: e.target.checked }))}
                                        style={{ marginTop: 3, width: 16, height: 16 }}
                                    />
                                    <span>
                                        <span style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#111827' }}>
                                            Show usage bill on Billing page
                                        </span>
                                        <span style={{ display: 'block', fontSize: 13, color: '#6b7280', marginTop: 4, lineHeight: 1.45 }}>
                                            Off = Usage per app shows counts only. On = each metric shows count × rate = amount, plus a Bill column.
                                        </span>
                                    </span>
                                </label>
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {deleteModal.show && deleteModal.user && (
                <Modal title="Delete User?" onClose={closeDeleteModal} maxWidth="450px" footer={
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button type="button" onClick={closeDeleteModal} disabled={deleteModal.loading} style={{ flex: 1, padding: '12px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '10px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>Cancel</button>
                        <button type="button" onClick={handleDelete} disabled={deleteModal.loading} style={{ flex: 1, padding: '12px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', cursor: deleteModal.loading ? 'not-allowed' : 'pointer', fontSize: '14px', fontWeight: '500', opacity: deleteModal.loading ? 0.7 : 1 }}>
                            {deleteModal.loading ? 'Deleting...' : 'Yes, Delete User'}
                        </button>
                    </div>
                }>
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ width: '64px', height: '64px', background: '#fef2f2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                            <MdDelete size={32} color="#ef4444" />
                        </div>
                        <p style={{ color: '#6b7280', marginBottom: '12px', fontSize: '15px' }}>Are you sure you want to delete this user?</p>
                        <div style={{ background: '#f9fafb', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                            <div style={{ fontWeight: '600', color: '#1a1a1a' }}>{deleteModal.user.first_name || ''} {deleteModal.user.last_name || ''}</div>
                            <div style={{ fontSize: '13px', color: '#6b7280' }}>{deleteModal.user.email}</div>
                        </div>
                        <p style={{ color: '#ef4444', fontSize: '13px', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', gap: '6px', textAlign: 'left' }}>
                            <MdWarningAmber size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                            <span>This action cannot be undone. All user data, applications, and associated records will be permanently removed.</span>
                        </p>
                    </div>
                </Modal>
            )}

            {alertModal.show && (
                <Modal
                    title={alertModal.title}
                    onClose={() => setAlertModal({ show: false, type: '', title: '', message: '', apps: [] })}
                    maxWidth="480px"
                    footer={
                        <button type="button" onClick={() => setAlertModal({ show: false, type: '', title: '', message: '', apps: [] })} style={{ padding: '10px 18px', background: alertModal.type === 'success' ? '#16a34a' : '#4f46e5', color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}>
                            {alertModal.type === 'success' ? 'Done' : 'Got it'}
                        </button>
                    }
                >
                    <div style={{ textAlign: 'center' }}>
                        <div style={{ width: '64px', height: '64px', background: alertModal.type === 'success' ? '#dcfce7' : '#fef2f2', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                            {alertModal.type === 'success' ? <MdCheckCircle size={32} color="#16a34a" /> : <MdErrorOutline size={32} color="#ef4444" />}
                        </div>
                        <p style={{ color: '#6b7280', marginBottom: alertModal.apps.length > 0 ? '16px' : 0, fontSize: '15px', lineHeight: '1.5' }}>{alertModal.message}</p>
                        {alertModal.apps.length > 0 && (
                            <div style={{ background: '#fef2f2', borderRadius: '10px', padding: '16px', marginBottom: '24px', textAlign: 'left' }}>
                                <div style={{ fontSize: '13px', fontWeight: '600', color: '#991b1b', marginBottom: '10px' }}>Applications with Active Users:</div>
                                {alertModal.apps.map((app, index) => (
                                    <div key={index} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: index < alertModal.apps.length - 1 ? '1px solid #fecaca' : 'none' }}>
                                        <span style={{ color: '#374151', fontSize: '14px' }}>{app.name}</span>
                                        <span style={{ background: '#fee2e2', color: '#991b1b', padding: '2px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '500' }}>
                                            {app.activeUsers} active user{app.activeUsers > 1 ? 's' : ''}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </Modal>
            )}

            {openActionItem && dropdownPosition && createPortal(
                <div ref={dropdownRef} style={{ position: 'fixed', left: dropdownPosition.left, top: dropdownPosition.top, zIndex: 10000, minWidth: '240px' }}>
                    <div className="dropdown-menu-subscription">
                        {getUserActionOptions(openActionItem).map((opt) => (
                            <button
                                key={opt.key}
                                type="button"
                                className={opt.danger ? 'delete-class' : ''}
                                onClick={() => handleDropdownAction(opt.action)}
                                style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', textAlign: 'left' }}
                            >
                                <span style={{ marginRight: '8px', flexShrink: 0, display: 'flex' }}>{opt.icon}</span>
                                <span style={{ fontSize: '0.875rem' }}>{opt.label}</span>
                            </button>
                        ))}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
};

export default AdminUsers;
